import { ReactComponent as ArrowRight16 } from "assets/svg/arrow-right-16.svg";
import IconButton from "components/icon-button";
import LazyImage from "components/lazy-image";
import { useTimeOnPageContext } from "contexts/time-on-page";
import { useEffect, useMemo, useState } from "react";
import { Col, Row, Tab, Tabs } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { AmplitudePageName, track } from "utils/amplitude";
import { GCalConnectButton } from "./GCalConnectButton";
import styles from "./Integration.module.scss";
import { IntegrationConnectButton } from "./IntegrationButtons";
import { GetTags } from "./IntegrationTags";
import ZoomConnectButton from "./ZoomConnectButton";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-integration";

const GOOGLE_CALENDAR = "/img/integration/google-calendar.png";
const CHROME = "/img/integration/chrome.png";
const GOOGLE_MEET = "/img/integration/google-meet.png";
const QUICKBOOK_ONLINE = "/img/integration/quickbooks-online.png";
const XERO = "/img/integration/xero.png";
const ZOOM = "/img/integration/zoom.png";
const OUTLOOK = "/img/integration/outlook.png";
const STRIPE = "/img/integration/stripe.png";
const GUSTO = "/img/integration/gusto.png";

const INTEGRATION_OPTIONS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "calendar", label: "Calendar" },
  { value: "apps", label: "Apps" },
];

type IntegratedApp = {
  icon: JSX.Element;
  label: string;
  description: string;
  internalLink?: boolean;
  category?: string[];
  link?: string;
};

const defaultApps: IntegratedApp[] = [
  {
    icon: <LazyImage src={GOOGLE_CALENDAR} width={48} height={48} />,
    label: "Google Calendar",
    description:
      "Leverage the simplicity of Google Calendar with either one-way or two-way sync.",

    category: ["all", "calendar"],
    internalLink: false,
  },

  {
    icon: <LazyImage src={CHROME} width={48} height={48} />,
    label: "Chrome Browser Extension",
    description:
      "Connect with Orchid AI: Transform your workflow with AI-assisted clinical note-taking.",

    category: ["all", "apps"],
    internalLink: false,
  },

  {
    icon: <LazyImage src={GOOGLE_MEET} width={48} height={48} />,
    label: "Google Meet",
    description: "Automatically include Google Meet details in Orchid events.",

    category: ["all", "apps"],
  },

  {
    icon: <LazyImage src={OUTLOOK} width={48} height={48} />,
    label: "Outlook",
    description: "Seamlessly sync your appointments to your Outlook calendar.",

    category: ["all", "calendar"],
  },

  {
    icon: <LazyImage src={STRIPE} width={48} height={48} />,
    label: "Stripe",
    description:
      "Simplify billing and reduce no-shows with an integrated Stripe account.",

    category: ["all", "apps"],
  },

  {
    icon: <LazyImage src={ZOOM} width={48} height={48} />,
    label: "Zoom",
    description:
      "Effortlessly generate Zoom links for your Orchid telehealth appointments.",

    internalLink: true,
    category: ["all", "apps"],
  },

  {
    icon: <LazyImage src={GUSTO} width={48} height={48} />,
    label: "Gusto",
    description:
      "Connect with an all-in-one HR platform to help manage your team at your fingertips.",

    category: ["all", "apps"],
  },

  {
    icon: <LazyImage src={QUICKBOOK_ONLINE} width={48} height={48} />,
    label: "Quickbooks Online",
    description:
      "Orchid works with Quickbooks Online, Intuit’s small web-based business accounting platform.",
    category: ["all", "apps"],
  },

  {
    icon: <LazyImage src={XERO} width={48} height={48} />,
    label: "Xero",
    description:
      "Integrate seamlessly with Xero, a cloud-based accounting app for small business.",

    category: ["all", "apps"],
  },
];

const Integration = () => {
  const { setPage: timeOnPageSetPage } = useTimeOnPageContext();

  useEffect(() => timeOnPageSetPage(AMPLITUDE_PAGE_NAME), [timeOnPageSetPage]);
  const [searchTerm] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get("tab") || "all",
    };
  }, [location.search]);
  const [tab, setTab] = useState(activeTab || "all");

  // useEffect(() => {
  //   if (!location.search.includes("tab")) {
  //     history.replace(`/pro-dashboard/settings/integrations?tab=all`);
  //   }
  // }, [location.search, history]);

  const filteredApps = useMemo(() => {
    if (activeTab.toLowerCase() === "all") {
      return defaultApps;
    } else {
      return defaultApps.filter(
        (el) => el.category && el.category.includes(activeTab.toLowerCase()),
      );
    }
  }, [activeTab]);

  return (
    <div className={styles["integration"]}>
      <div className={styles["integration__header"]}>
        <div className={styles["integration__title"]}>Integrations</div>
        {INTEGRATION_OPTIONS.length ? (
          <Tabs
            activeKey={tab}
            id="integrations-tabs-controller"
            onSelect={(k) => {
              setTab((s) => k || s);
              navigate(`/pro-dashboard/settings/integrations?tab=${k}`, { replace: true });
              track(AMPLITUDE_PAGE_NAME, `${k}-tab`, "click");
            }}
            className={styles["integration__tabs"]}
          >
            {INTEGRATION_OPTIONS.map((el, i) => {
              return (
                <Tab
                  key={i}
                  eventKey={el.label.toLowerCase()}
                  title={el.label}
                ></Tab>
              );
            })}
          </Tabs>
        ) : null}
      </div>
      <div className={styles["integration__body"]}>
        {/* <div className={styles["integration__section-title"]}>
            {_.capitalize(tab)}
          </div> */}
        <div className={styles["integration__section-content"]}>
          Explore Orchid's integrations to seamlessly connect your favorite
          tools with Orchid and enhance your practice's efficiency.
        </div>
        {/* <label className={styles["integration__search-container"]}>
            {!searchTerm && <MagnifyingGlass />}
            <input
              className="form-control"
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                debouncedTrack(
                  AMPLITUDE_PAGE_NAME,
                  "search-by-app-name",
                  "keypress",
                );
              }}
              placeholder="Find an app"
            />
          </label> */}
        <Row>
          {filteredApps.map((app, index) => {
            return app.label
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ? (
              <Col key={index} md={4} className={styles["integration__col"]}>
                <div className={styles["integration__card"]}>
                  <GetTags appLabel={app.label} />

                  <div className={styles["integration__card-icon"]}>
                    {app.icon}
                  </div>
                  <div className={styles["integration__card-label"]}>
                    {app.label}
                  </div>
                  <div className={styles["integration__card-description"]}>
                    {app.description}
                  </div>

                  {app.label === "Google Calendar" && <GCalConnectButton />}

                  {app.label === "Zoom" && <ZoomConnectButton />}

                  {app.label !== "Google Calendar" && app.label !== "Zoom" && (
                    <IntegrationConnectButton appLabel={app.label} />
                  )}
                </div>
              </Col>
            ) : null;
          })}

          {!searchTerm && (
            <Col md={4} className={styles["integration__col"]}>
              <div className={styles["build-with-us__card"]}>
                <div className={styles["build-with-us__header"]}>
                  BUILD WITH US
                </div>
                <div className={styles["build-with-us__subheader"]}>
                  Got a tool you love? Tell us!
                </div>
                <div className={styles["integration__card-description"]}>
                  Request an integration and expand your toolbox. Help us help
                  you work smarter.
                </div>
                <IconButton
                  as="a"
                  href="mailto:info@orchid.health"
                  className={styles["integration__card-configure-link"]}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "request-now-button", "click")
                  }
                >
                  <ArrowRight16 />
                  Request Now
                </IconButton>
              </div>
            </Col>
          )}
        </Row>
      </div>
      <div className={styles["integration__footer"]}>
        <div>©2026 Orchid Exchange Inc. All rights reserved</div>
      </div>
    </div>
  );
};

export default Integration;
